import dayjs from 'dayjs';

import { Notification } from '@features/notifications';
import { notificationType } from '@features/notifications/NotificationsEnum';

export const formNotificationTitle = (notification: Notification) => {
  switch (notification.notification_type) {
    case 'APPLICANT COMPLETED ONBOARDING':
      return 'Onboadring Documents Complete';
    case 'NEW MESSAGE':
      return 'New Message';
    default:
      return notification.message;
  }
};

export const formNotificationSecondaryText = (notification: Notification) => {
  const { associated_user, application, date_created } = notification;

  return [
    associated_user?.name,
    application?.position,
    dayjs(date_created).format('MM/DD/YYYY - h:mm A'),
  ]
    .filter(Boolean)
    .join(' - ');
};

export function isWithinSevenDays(dtTimestamp: string | number | Date) {
  if (!dtTimestamp) return false; // Handle null or undefined timestamp
  const currentDate = new Date();
  const givenDate = new Date(dtTimestamp);

  // Calculate the difference in milliseconds
  const differenceInMillis = currentDate.getTime() - givenDate.getTime();

  // Convert milliseconds to days
  const differenceInDays = differenceInMillis / (1000 * 60 * 60 * 24);

  return differenceInDays <= 7;
}
export function groupNotifications(notifications: Notification[]) {
  const grouped: Record<string, Notification[]> = {};

  for (const notif of notifications) {
    const key = notif.notification_type;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(notif);
  }

  return grouped;
}
type GroupKey =
  | 'OPEN_INTERVIEW_SIGNUP'
  | (typeof notificationType)[keyof typeof notificationType];

export function getNotificationGroupKey(notification: Notification): GroupKey {
  const msg = notification.message.toLowerCase();

  // Custom content-based groupings
  if (msg.includes('signed up for an open interview') || msg.includes('confirmed your interview invite') || msg.includes('has selected a time slot')) {
    return 'OPEN_INTERVIEW_SIGNUP';
  }
  if (msg.includes('<b>') || msg.includes('<strong>')) {
    return 'INTERVIEW REMINDERS';
  }

  if (msg.includes('a new applicant has been saved.')) {
    return 'NEW_APPLICANT_SAVED';
  }

  if (msg.includes('has been declined')) {
    return 'JOB OFFER DECLINED';
  }

  if (msg.includes('has been withdrawn')) {
    return 'APPLICATION WITHDRAWN';
  }

  if (msg.includes('your application has been archived')) {
    return 'APPLICATION ARCHIVED';
  }

  if (msg.includes('but no interviews available')) {
    return 'NO BLOCKS AVAILABLE';
  }

  if (msg.includes('has been accepted')) {
    return 'JOB OFFER ACCEPTED';
  }

  // Reclassify certain AUTO_ACTION_PERFORMED messages
  if (
    notification.notification_type === notificationType.AUTO_ACTION_PERFORMED &&
    msg.includes('a new applicant') &&
    msg.includes('invited to interview')
  ) {
    return notificationType.PENDING_INTERVIEW_INVITE;
  }

  // Fallback: use the default notification type
  return notification.notification_type as GroupKey;
}
  

export function groupNotificationsSmart(notifications: Notification[]) {
  const grouped: Record<string, Notification[]> = {};
  if (!notifications || notifications.length === 0) return grouped;

  for (const notif of notifications?.filter((item) => !item.is_read)) {
    const groupKey = getNotificationGroupKey(notif);
    if (!grouped[groupKey]) grouped[groupKey] = [];
    grouped[groupKey].push(notif);
  }

  return grouped;
}

export function getGroupSummaryText(type: string, count: number): string {
  switch (type) {
    case 'OPEN_INTERVIEW_SIGNUP':
      return `${count} applicants signed up for an interview.`;
    case 'INTERVIEW REMINDERS':
      return `${count} interview reminders.`;
    case 'NEW_APPLICANT_SAVED':
      return `${count} new applicants have been saved.`;
    case 'but no interviews available':
      return `${count} no interviews available notifications.`;
    case 'JOB OFFER DECLINED':
      return `${count} Job offers have been declined.`;
    case 'JOB OFFER ACCEPTED':
      return `${count} Job offers have been accepted.`;
    case 'APPLICATION ARCHIVED':
      return `${count} applications have been archived.`;
    case 'APPLICATION WITHDRAWN':
      return `${count} applications have been withdrawn`;
    case notificationType.PENDING_INTERVIEW_INVITE:
      return `${count} new applicants have been invited to interview.`;
    case notificationType.NEW_MESSAGE:
      return `${count} new messages received.`;
    default:
      return `${count} ${type.toLowerCase().replace(/_/g, ' ')} notifications.`;
  }
}
